import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import Tooltip from 'components/Tooltip';
import {withScrollbar} from 'components/withScrollbar';
import {DATE_FORMAT_API} from 'utils/datetime';
import {formatMoney} from 'utils/money';

import {buildEmptyTableColumn} from 'ducks/reporting/revenue/utils';
import {OVERVIEW_RESOLUTION_OPTIONS} from 'ducks/reporting';

import './styles.css';

const formatNumber = (value: number) => `${Math.round(value / 1000)}k`;

const SideMenuRow = ({label}) => (
  <div className="SideMenu__table__row colab-bold colab-centered-v">
    {label}
  </div>
);

const SideMenu = ({tableRows, maxValue = 0}) => (
  <div className="SideMenu">
    <div className="SideMenu__chart">
      <div>{formatNumber(maxValue)}</div>
      <div>{formatNumber(maxValue * 3 / 4)}</div>
      <div>{formatNumber(maxValue / 2)}</div>
      <div>{formatNumber(maxValue * 1 / 4)}</div>
      <div>0</div>
    </div>
    <div className="SideMenu__table">
      <SideMenuRow label="Total Projected £" />
      {tableRows.map(row => (
        <SideMenuRow label={row.labelForSideMenu} key={row.key} />
      ))}
    </div>
  </div>
);

const ColumnRow = ({value, tooltipData}) => (
  <div
    className="ColumnRow colab-centered"
    data-tip={JSON.stringify(tooltipData)}
    data-for="Chart__tooltip">
    {formatNumber(value)}
  </div>
);

const ChartTooltip = () => (
  <Tooltip
    place="right"
    type="dark"
    effect="solid"
    id="Chart__tooltip"
    className="Chart__tooltip"
    getContent={stringifiedData => {
      const data = stringifiedData && JSON.parse(stringifiedData);
      const shouldRender = !_.isEmpty(data);

      return (
        shouldRender &&
        data.map(row => (
          <div className="colab-row-space-between" key={row.label}>
            <div className="Chart__tooltip__label">{row.label}</div>
            <div className="colab-bold">£{formatMoney(row.value)}</div>
          </div>
        ))
      );
    }}
  />
);

const ColumnBar = ({maxValue, segments, tableRows}) => {
  const maxBarHeight = 200;

  return (
    <div className="Column__barContainer">
      <div className="Column__bar">
        {tableRows.map(tableRow => {
          const value = segments[tableRow.key].value || 0;

          return (
            <div
              key={tableRow.key}
              className={classNames(
                'Column__bar__segment',
                `Column__bar__segment--${tableRow.key}`
              )}
              style={{
                height: value / maxValue * maxBarHeight
              }}
              data-tip={JSON.stringify(segments[tableRow.key].tooltipData)}
              data-for="Chart__tooltip"
            />
          );
        })}
      </div>
    </div>
  );
};

const ColumnDelimeters = () => {
  return (
    <div className="Column__delimeterContainer">
      <div />
      <div />
      <div />
      <div />
      <div />
    </div>
  );
};

const getColumnBarLabel = ({date, resolution}) => {
  if (date === '') {
    return {label: '', subLabel: ''};
  }

  let subLabel: any = moment(date, DATE_FORMAT_API).year();

  if (resolution === OVERVIEW_RESOLUTION_OPTIONS.DAILY) {
    const label = moment(date, DATE_FORMAT_API).format('DD/MM');
    const subLabel = moment(date, DATE_FORMAT_API).year();

    return {label, subLabel};
  }

  if (resolution === OVERVIEW_RESOLUTION_OPTIONS.MONTHLY) {
    const startMonth = moment(date, DATE_FORMAT_API).format('MMM'); // Jan, Feb, ... Dec

    return {label: startMonth, subLabel};
  }

  if (resolution === OVERVIEW_RESOLUTION_OPTIONS.QUARTERLY) {
    const quarter = moment(date, DATE_FORMAT_API).quarter(); // Q1, Q2, Q3 ...

    return {label: `Q${quarter}`, subLabel};
  }

  if (resolution === OVERVIEW_RESOLUTION_OPTIONS.CALENDAR_YEAR) {
    return {label: moment(date, DATE_FORMAT_API).year(), subLabel: ''};
  }

  if (resolution === OVERVIEW_RESOLUTION_OPTIONS.FINANCIAL_YEAR) {
    const yearOfFinancialYearStart = moment(date, DATE_FORMAT_API).year();
    const yearOfFinancialYearEnd = yearOfFinancialYearStart + 1;

    return {
      label: '',
      subLabel: `${yearOfFinancialYearStart}-${yearOfFinancialYearEnd}`
    };
  }

  if (resolution === OVERVIEW_RESOLUTION_OPTIONS.FINANCIAL_YEARS_MONTHLY) {
    const startMonth = moment(date, DATE_FORMAT_API).format('MMM'); // Jan, Feb, ... Dec

    return {label: startMonth, subLabel};
  }

  return {label: '', subLabel: ''};
};

const ColumnBarLabel = ({date, resolution}) => {
  const {label, subLabel} = getColumnBarLabel({date, resolution});

  return (
    <div className="Column__chart__timeframe">
      <div className="colab-bold">{label}</div>
      <div>{subLabel}</div>
    </div>
  );
};

const Column = ({dataPoint, maxValue, resolution, tableRows}) => {
  const reportRows = dataPoint.report;

  // This will cast `reportRows` from an object to a list and then sum the values
  const totalRow = _.sumBy(_.map(reportRows), (x: any) => x.value);

  const firstTableRow = reportRows[tableRows[0].key]; // confirmed or short let row in table
  const secondTableRow = reportRows[tableRows[1].key]; // reserved or long let row in table

  const firstRowTooltipData = firstTableRow.tooltipData;
  const secondRowTooltipData = secondTableRow.tooltipData;

  const totalRowTooltipData = !_.isEmpty(firstRowTooltipData) &&
    !_.isEmpty(secondRowTooltipData) && [
      {label: 'Total Projected', value: totalRow},
      {
        label: firstRowTooltipData[0].label,
        value: firstRowTooltipData[0].value
      },
      {
        label: secondRowTooltipData[0].label,
        value: secondRowTooltipData[0].value
      }
    ];

  return (
    <div className="Column">
      <div className="Column__chart">
        <ColumnDelimeters />
        <ColumnBar
          maxValue={maxValue}
          segments={reportRows}
          tableRows={tableRows}
        />
        <ColumnBarLabel resolution={resolution} date={dataPoint.date} />
      </div>
      <div className="Column__table">
        <ColumnRow value={totalRow} tooltipData={totalRowTooltipData} />
        {tableRows.map(tableRow => (
          <ColumnRow
            key={tableRow.key}
            value={reportRows[tableRow.key].value}
            tooltipData={reportRows[tableRow.key].tooltipData}
          />
        ))}
      </div>
    </div>
  );
};

const Legend = ({rows}) => (
  <div className="Legend">
    <div className="colab-elements-row">
      {rows.map(row => (
        <div className="colab-centered" key={row.key}>
          <div
            className={classNames(
              'Legend__circle',
              `Legend__circle--${row.key}`
            )}
          />
          <div>{row.labelForLegend}</div>
        </div>
      ))}
    </div>
  </div>
);

class RevenueOverviewChart extends React.Component<any> {
  containerRef: any = React.createRef();

  componentDidMount() {
    this.props.setScrollableContainerRef(this.containerRef.current);
  }

  render() {
    const {revenue, resolution, HorizontalScrollbar} = this.props;
    const maxValue = revenue.maxPossibleRevenue;

    let reportData = revenue.bookingState.data;
    if (reportData.length > 0 && reportData.length < 20) {
      reportData = [
        ...revenue.bookingState.data,
        ..._.range(reportData.length, 19).map(() => buildEmptyTableColumn())
      ];
    }

    return (
      <div className="RevenueOverviewChart">
        <SideMenu
          tableRows={revenue.bookingState.tableRows}
          maxValue={maxValue}
        />
        <div className="columnsWrapper">
          <div className="columnsContainer" ref={this.containerRef}>
            {reportData.map((dataPoint, index) => (
              <Column
                key={dataPoint.date || index}
                dataPoint={dataPoint}
                maxValue={maxValue}
                resolution={resolution}
                tableRows={revenue.bookingState.tableRows}
              />
            ))}
            {HorizontalScrollbar}
          </div>
        </div>
        <Legend rows={revenue.bookingState.tableRows} />
        <div className="bookingState colab-bold">Booking State</div>
        <ChartTooltip />
      </div>
    );
  }
}

export default withScrollbar(RevenueOverviewChart);
